import React from 'react';
import { useTownState } from '../context/TownStateContext';
import CharacterComponent from './CharacterComponent';
import { Zone } from '../types/Town';

const Town: React.FC = () => {
    const { characters, zones, selectedCharacter, setSelectedCharacter, moveCharacter, dataSource } = useTownState();
    
    const handleZoneClick = (e: React.MouseEvent, zone: Zone) => {
        e.stopPropagation();
        if (selectedCharacter) {
            
            // STEP 1: Get all characters currently in this zone
            const charactersInZone = characters.filter(char => 
                char.position.x >= zone.position.x && 
                char.position.x < zone.position.x + zone.position.width &&
                char.position.y >= zone.position.y && 
                char.position.y < zone.position.y + zone.position.height
            );
            
            // STEP 2: Find the first available position in the zone
            let targetX = zone.position.x;
            let targetY = zone.position.y;
            let positionFound = false;
            
            // Try each cell in the zone until we find an empty one
            for (let y = zone.position.y; y < zone.position.y + zone.position.height && !positionFound; y++) {
                for (let x = zone.position.x; x < zone.position.x + zone.position.width && !positionFound; x++) {
                    // Check if any character is already at this position
                    const isOccupied = characters.some(char => 
                        char.position.x === x && 
                        char.position.y === y
                    );
                    
                    if (!isOccupied) {
                        targetX = x;
                        targetY = y;
                        positionFound = true;
                    }
                }
            }
            
            // STEP 3: If zone is full, position at entrance
            if (!positionFound) {
                targetX = zone.position.x;
                targetY = zone.position.y + zone.position.height; // Just outside zone
            }
            
            console.log(`Moving ${selectedCharacter.name} to ${zone.name} at position (${targetX}, ${targetY})`);
            
            // Mover el personaje a la posición seleccionada
            if (moveCharacter) {
                moveCharacter(selectedCharacter.id, { x: targetX, y: targetY });
            }
            setSelectedCharacter(null);
        }
    };

    const handleFreeClick = (e: React.MouseEvent) => {
        if (selectedCharacter && e.target === e.currentTarget) {
            const rect = e.currentTarget.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            
            const GRID_SIZE = 32;
            const GAP_SIZE = 1;
            const TOTAL_GAPS = (GRID_SIZE - 1) * GAP_SIZE;
            
            const cellWidth = (rect.width - TOTAL_GAPS) / GRID_SIZE;
            const cellHeight = (rect.height - TOTAL_GAPS) / GRID_SIZE;
            
            const gridX = Math.floor(clickX / (cellWidth + GAP_SIZE));
            const gridY = Math.floor(clickY / (cellHeight + GAP_SIZE));
            
            if (gridX >= 0 && gridX < GRID_SIZE && gridY >= 0 && gridY < GRID_SIZE) {
                // Mover el personaje a la posición en la cuadrícula
                if (moveCharacter) {
                    moveCharacter(selectedCharacter.id, { x: gridX, y: gridY });
                }
            }
        }
        if (e.target === e.currentTarget) {
            setSelectedCharacter(null);
        }
    };

    return (
        <div 
            className={`town-container ${selectedCharacter ? 'character-selected' : ''}`} 
            onClick={handleFreeClick}
        >

            {zones.map(z => (
              <div 
                key={z.id} 
                className={`building ${z.type}`} // Kept 'building' class for CSS compatibility for now
                style={{
                    gridColumn: `${z.position.x + 1} / span ${z.position.width}`,
                    gridRow: `${z.position.y + 1} / span ${z.position.height}`,
                }}
                onClick={(e) => handleZoneClick(e, z)}
                title={`${z.name} - Click to move here`}
              >
                  <span className="zone-label">{z.name}</span>
              </div>
            ))}
            {characters.map(char => (
                <CharacterComponent 
                    key={char.id} 
                    character={char} 
                    isSelected={selectedCharacter?.id === char.id}
                    onSelect={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setSelectedCharacter(char);
                    }}
                />
            ))}
        </div>
    );
};

export default Town;